import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon, Functions as FunctionsIcon } from '@mui/icons-material';
import useThemeStyles from '../../../hooks/useThemeStyles';
import { evaluateFormula, extractVariables } from '../../../utils/gamedesign/evaluateFormula';

/**
 * Dumb formula card: name, expression, description, auto-detected variables
 * with test values, and a live evaluation result.
 */
const FormulaCard = ({ formula, onChange, onDelete }) => {
  const { colors } = useThemeStyles();

  const textFieldSx = {
    '& .MuiInputLabel-root': { color: colors.text.secondary },
    input: { color: colors.text.primary },
    textarea: { color: colors.text.primary },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors.border.subtle },
      '&:hover fieldset': { borderColor: colors.interactive.primary },
      '&.Mui-focused fieldset': { borderColor: colors.interactive.primary },
    },
  };

  const variableNames = useMemo(() => extractVariables(formula.expression), [formula.expression]);

  const variableMap = useMemo(() => {
    const map = {};
    (formula.variables || []).forEach((variable) => {
      map[variable.name] = variable.value;
    });
    return map;
  }, [formula.variables]);

  const result = useMemo(
    () => evaluateFormula(formula.expression, variableMap),
    [formula.expression, variableMap]
  );

  const updateVariable = (name, rawValue) => {
    const value = rawValue === '' ? 0 : Number(rawValue);
    const existing = formula.variables || [];
    const has = existing.some((v) => v.name === name);
    const variables = has
      ? existing.map((v) => (v.name === name ? { ...v, value } : v))
      : [...existing, { name, value }];
    onChange({ ...formula, variables });
  };

  return (
    <Card
      variant="outlined"
      sx={{ backgroundColor: 'transparent', borderColor: colors.border.subtle }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FunctionsIcon sx={{ color: colors.interactive.primary }} />
          <TextField
            label="Formula name"
            size="small"
            value={formula.name}
            onChange={(e) => onChange({ ...formula, name: e.target.value })}
            sx={{ ...textFieldSx, flex: 1 }}
          />
          <Tooltip title="Delete formula">
            <IconButton size="small" onClick={onDelete} sx={{ color: colors.icon.secondary }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <TextField
          label="Expression"
          size="small"
          value={formula.expression}
          onChange={(e) => onChange({ ...formula, expression: e.target.value })}
          placeholder="e.g. floor(attack * power / defense) + 2"
          fullWidth
          multiline
          maxRows={3}
          sx={textFieldSx}
        />

        <TextField
          label="Description"
          size="small"
          value={formula.description || ''}
          onChange={(e) => onChange({ ...formula, description: e.target.value })}
          fullWidth
          multiline
          maxRows={3}
          sx={textFieldSx}
        />

        {variableNames.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ color: colors.text.secondary }}>
              Test values
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {variableNames.map((name) => (
                <TextField
                  key={name}
                  label={name}
                  size="small"
                  type="number"
                  value={variableMap[name] ?? ''}
                  onChange={(e) => updateVariable(name, e.target.value)}
                  sx={{ ...textFieldSx, width: 110 }}
                />
              ))}
            </Box>
          </Box>
        )}

        {formula.expression && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Result:
            </Typography>
            {result.error ? (
              <Chip
                label={result.error}
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${colors.semantic.error}`,
                  color: colors.semantic.error,
                }}
              />
            ) : (
              <Chip
                label={result.value === null ? '—' : result.value}
                size="small"
                sx={{
                  backgroundColor: colors.sidebar.headerBackground,
                  color: colors.text.primary,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

FormulaCard.propTypes = {
  formula: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    expression: PropTypes.string,
    description: PropTypes.string,
    variables: PropTypes.arrayOf(
      PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })
    ),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default FormulaCard;
